import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { Database, Json } from "@/integrations/supabase/types";
import { readSharedEdgeCache, writeSharedEdgeCache } from "@/server/site/edge-cache";

type SeoPageOverrideRow = Database["public"]["Tables"]["seo_page_overrides"]["Row"];

export interface SeoPageOverride {
  path: string;
  pageType: "estatica" | "servicio" | "geo" | "blog";
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalPath: string | null;
  robotsIndex: boolean;
  robotsFollow: boolean;
  ogImage: string | null;
  schemaType: "service" | "article" | "faqpage" | null;
  schemaData: Json;
  updatedBy: string | null;
  updatedByEmail: string | null;
  updatedAt: string;
}

export function mapOverrideRow(row: SeoPageOverrideRow): SeoPageOverride {
  return {
    path: row.path,
    pageType: row.page_type as SeoPageOverride["pageType"],
    metaTitle: row.meta_title,
    metaDescription: row.meta_description,
    canonicalPath: row.canonical_path,
    robotsIndex: row.robots_index,
    robotsFollow: row.robots_follow,
    ogImage: row.og_image,
    schemaType: row.schema_type as SeoPageOverride["schemaType"],
    schemaData: row.schema_data,
    updatedBy: row.updated_by,
    updatedByEmail: row.updated_by_email,
    updatedAt: row.updated_at,
  };
}

// Cache en memoria por path (mismo patrón que getSiteImageSlots, adaptado a
// clave variable en vez de fija) — ver contact.fns.ts para la nota completa
// sobre por qué no hay hook de invalidación cross-worker. Se cachea también
// el resultado `null` (path sin override, el caso de "/" hoy) para que rutas
// sin override dejen de golpear Supabase en cada visita. Sin límite de
// tamaño en el Map: el sitio tiene un número acotado de rutas reales (home,
// 7 servicios, ~40 posts de blog, páginas geo) — no es un riesgo práctico.
const overrideCache = new Map<string, { data: SeoPageOverride | null; expiresAt: number }>();
const OVERRIDE_CACHE_TTL = 60_000;

function edgeCacheKeyFor(path: string): string {
  return `public-seo-override:${path}`;
}

// El valor cacheado legítimo puede ser `null` (path sin override), así que
// se envuelve en un objeto — si no, `readSharedEdgeCache` no podría
// distinguir "no está en caché" de "está en caché y es null".
interface OverrideEdgeCacheEntry {
  value: SeoPageOverride | null;
}

/**
 * Lectura pública (sin autenticación) del override SEO de una página, por
 * path exacto. Se usa en el beforeLoad de la ruta raíz para SSR (igual que
 * getPublicContactInfo) — beforeLoad corre tanto en cliente como en servidor,
 * por eso esta consulta a Supabase MUST pasar por un createServerFn en vez de
 * importarse directo (client.server.ts no puede llegar al bundle del cliente).
 * Degrada a null ante cualquier error: el sitio público nunca debe romper por
 * este lookup.
 */
// Validador manual en vez de zod a propósito: los validators de server fns
// viajan también al bundle del cliente, y este archivo se importa desde el
// __root del sitio público — un schema de zod acá arrastraba ~16 KB (gzip)
// de zod al JS inicial de TODAS las páginas públicas. Mismo criterio en los
// demás .fns públicos (blog-public-detail, promotoras-gallery).
export const getSeoOverrideForPath = createServerFn({ method: "GET" })
  .validator((input: unknown): { path: string } => {
    const path = (input as { path?: unknown } | null)?.path;
    if (typeof path !== "string") throw new Error("path inválido");
    return { path };
  })
  .handler(async ({ data }): Promise<SeoPageOverride | null> => {
    const cached = overrideCache.get(data.path);
    if (cached && Date.now() < cached.expiresAt) return cached.data;

    const edgeKey = edgeCacheKeyFor(data.path);
    const fromEdge = await readSharedEdgeCache<OverrideEdgeCacheEntry>(edgeKey);
    if (fromEdge) {
      overrideCache.set(data.path, {
        data: fromEdge.value,
        expiresAt: Date.now() + OVERRIDE_CACHE_TTL,
      });
      return fromEdge.value;
    }

    try {
      const { data: row, error } = await supabaseAdmin
        .from("seo_page_overrides")
        .select("*")
        .eq("path", data.path)
        .maybeSingle();

      const resolved = error || !row ? null : mapOverrideRow(row);
      overrideCache.set(data.path, { data: resolved, expiresAt: Date.now() + OVERRIDE_CACHE_TTL });
      await writeSharedEdgeCache(
        edgeKey,
        { value: resolved } satisfies OverrideEdgeCacheEntry,
        OVERRIDE_CACHE_TTL / 1000,
      );
      return resolved;
    } catch {
      overrideCache.set(data.path, { data: null, expiresAt: Date.now() + OVERRIDE_CACHE_TTL });
      return null;
    }
  });
